/* =============================================================================
   CORE / PRINTER-ADAPTER — Drucker-Pipeline (Phase 12.1)
   =============================================================================
   Strategy-Pattern: Drei Backends, einheitliche API.

     1. MOCK    — loggt + emittiert `printer:mock-print`-Event mit Preview-HTML.
                  Default-Backend wenn der Drucker nicht oder noch nicht
                  fertig konfiguriert ist. Zeigt im Cockpit einen Toast +
                  ein Preview-Modal.
     2. BRIDGE  — WebSocket-Client an die konfigurierte Endpoint-URL (z.B.
                  ws://192.168.1.42:9100 bei einem Raspberry-Pi-Bridge-Server).
                  Schickt JSON {kind, base64, meta}, erwartet kurze Quittung.
                  Heartbeat-Reconnect mit Backoff.
     3. CLOUD   — schreibt einen Druckjob in die Queue (RPC enqueue_print_job).
                  CloudPRNT-fähige Drucker (Star, Epson Connect) pollen die
                  Edge Function /functions/v1/printer-relay, die wiederum
                  claim_print_jobs ruft.

   Public API:
     listPrinters()
       → [{id, name, kind, backend, endpointUrl, authToken, paperWidthMm,
           isActive, deviceId, ...}]
     getPrinter(id)
     getRouting()
       → {autoOnPaid: boolean, kitchenOnConfirm: boolean, kitchenOnPreparing: boolean}
     printReceipt(order, opts)         — alle aktiven Receipt-Drucker
     printKitchenTicket(order, opts)   — alle aktiven Kitchen-Drucker
     testPrint(printerId)
     enqueue(printerId, jobPayload)    — low-level

   opts:
     - silent: boolean — keine Toasts (für Auto-Print-Hooks)

   Settings-Schema (in restaurants.settings):
     printers: [{
       id:           'pr_xxx',     // slot-id, stabil
       name:         'Theke',
       kind:         'receipt' | 'kitchen',
       backend:      'mock' | 'bridge' | 'cloud',
       endpointUrl:  string,        // bei bridge: ws://..., bei cloud: URL für Polling
       authToken:    string,        // optional
       paperWidthMm: 80 | 58,
       deviceId:     string,        // device-Identifier für CloudPRNT (Drucker-Serial)
       isActive:     boolean
     }]
     printerRouting: {
       autoOnPaid:        boolean,  // Bon nach paid drucken
       kitchenOnConfirm:  boolean,  // Küchen-Bon bei Status 'confirmed'
       kitchenOnPreparing:boolean   // Küchen-Bon bei Status 'preparing'
     }
============================================================================ */

import {
  renderReceiptEscpos,
  renderKitchenTicketEscpos,
  renderTestPageEscpos,
  renderPartialCorrectionEscpos
} from '../utils/escpos-renderer.js';
import { showToast } from '../utils/helpers.js';

// -----------------------------------------------------------------------------
// State-Zugriff
// -----------------------------------------------------------------------------
function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

function getSettings() { return (getState() && getState().settings) || {}; }

export function listPrinters() {
  const s = getSettings();
  const arr = Array.isArray(s.printers) ? s.printers : [];
  return arr.filter(p => p && typeof p === 'object');
}

export function getPrinter(id) {
  return listPrinters().find(p => p.id === id) || null;
}

export function getRouting() {
  const s = getSettings();
  const r = (s && s.printerRouting) || {};
  return {
    autoOnPaid:         !!r.autoOnPaid,
    kitchenOnConfirm:   !!r.kitchenOnConfirm,
    kitchenOnPreparing: !!r.kitchenOnPreparing
  };
}

function getRestaurantMeta() {
  const s = getSettings();
  const r = (s && s.restaurant) || {};
  return {
    legalName: r.legalName || s.restaurantName,
    name:      s.restaurantName,
    street:    r.street,
    zip:       r.zip,
    city:      r.city,
    phone:     s.restaurantPhone,
    ustId:     r.ustId,
    taxNumber: r.taxNumber
  };
}

// -----------------------------------------------------------------------------
// Backend-Strategien
// -----------------------------------------------------------------------------
const Backends = {
  async mock(printer, job, opts) {
    // S42 (L-10): Object-Dump nur in DEV — sonst spammt das die Production-
    // Console pro Mock-Print. Vite tree-shaked den Branch im Production-Build.
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
      console.info('[printer-adapter][mock]', printer.name, job.kind, {
        base64Length: (job.base64 || '').length,
        plainPreview: (job.plainText || '').slice(0, 80) + '…'
      });
    }
    // Phase 24 (Session 22 Nachschlag): silent-Flag respektiert jetzt auch
    // den Preview-Modal-Event. Bisher poppte der Mock-Preview auch bei
    // Auto-Triggern (kitchenOnConfirm, autoOnPaid) auf, obwohl die Caller
    // bewusst `silent: true` gesetzt haben — total irreführend für Bediener
    // im KDS/Bar-Flow. Jetzt: bei silent=true emittet das Modul nur ein
    // anderes Event `printer:mock-print-silent` für etwaige Tracker
    // (z.B. Audit), KEIN Preview-Modal. Manueller „Testdruck"-Button bleibt
    // unverändert (kein silent → Modal+Toast wie bisher).
    const silent = !!(opts && opts.silent);
    if (window.App && window.App.events && typeof window.App.events.emit === 'function') {
      try {
        await window.App.events.emit(silent ? 'printer:mock-print-silent' : 'printer:mock-print', {
          printerId:   printer.id,
          printerName: printer.name,
          kind:        job.kind,
          html:        job.html,
          plainText:   job.plainText,
          base64:      job.base64,
          orderId:     job.orderId
        });
      } catch (_) {}
    }
    if (!silent) {
      showToast(`[Mock] ${printer.name} — ${labelForKind(job.kind)} gedruckt (${(job.plainText || '').split('\n').length} Zeilen)`, 'info');
    }
    return { ok: true, backend: 'mock' };
  },

  async bridge(printer, job, opts) {
    // WebSocket pro Job aufbauen + sofort schließen. Bei aktiven Bridges
    // ließe sich später ein persistentes Connection-Pool nachrüsten.
    const url = printer.endpointUrl;
    if (!url || !/^wss?:\/\//.test(url)) {
      throw new Error('Bridge-URL fehlt oder ist kein WebSocket: ' + url);
    }
    return new Promise((resolve, reject) => {
      let ws, doneTimer;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        return reject(new Error('WebSocket-Konstruktor fehlgeschlagen: ' + e.message));
      }
      const cleanup = () => {
        clearTimeout(doneTimer);
        try { ws.close(); } catch (_) {}
      };
      ws.onopen = () => {
        const payload = {
          kind:         job.kind,
          base64:       job.base64,
          html:         job.html,
          token:        printer.authToken || null,
          printerId:    printer.id,
          paperWidthMm: printer.paperWidthMm || 80,
          deviceId:     printer.deviceId || null,
          ts:           new Date().toISOString()
        };
        try {
          ws.send(JSON.stringify(payload));
          // Geben dem Server 2.5s, dann gilt's als erfolgreich (fire-and-forget).
          doneTimer = setTimeout(() => {
            cleanup();
            if (!opts || !opts.silent) showToast(`[Bridge] ${printer.name} gedruckt`, 'success');
            resolve({ ok: true, backend: 'bridge' });
          }, 2500);
        } catch (e) {
          cleanup();
          reject(new Error('Bridge-Send fehlgeschlagen: ' + e.message));
        }
      };
      ws.onerror = () => {
        cleanup();
        reject(new Error('Bridge-WebSocket-Fehler ' + url));
      };
      ws.onmessage = (msg) => {
        // Optional: Server quittiert mit {ok:true}; behandeln wir wie Erfolg.
        try {
          const data = JSON.parse(msg.data);
          if (data && data.ok === false) {
            cleanup();
            return reject(new Error('Bridge-Fehler: ' + (data.error || 'unbekannt')));
          }
        } catch (_) {}
        cleanup();
        if (!opts || !opts.silent) showToast(`[Bridge] ${printer.name} gedruckt`, 'success');
        resolve({ ok: true, backend: 'bridge' });
      };
      // Hartes Timeout, falls onopen nie kommt (z.B. Netzwerkblockade)
      setTimeout(() => {
        if (ws.readyState !== 1 && ws.readyState !== 3) {
          cleanup();
          reject(new Error('Bridge-Connect-Timeout ' + url));
        }
      }, 5000);
    });
  },

  async cloud(printer, job, opts) {
    // Job in die print_jobs-Queue legen — Drucker pollt /printer-relay.
    const client = window.App && window.App.supabase;
    if (!client) throw new Error('Supabase-Client nicht initialisiert');
    const { data, error } = await client.rpc('enqueue_print_job', {
      p_printer_slot_id:    printer.id,
      p_kind:               job.kind,
      p_order_id:           job.orderId || null,
      p_payload_escpos_b64: job.base64 || null,
      p_payload_html:       job.html || null,
      p_payload_meta: {
        printerName:  printer.name,
        deviceId:     printer.deviceId || null,
        paperWidthMm: printer.paperWidthMm || 80
      }
    });
    if (error) throw new Error('enqueue_print_job: ' + error.message);
    if (!opts || !opts.silent) {
      showToast(`[Cloud] ${printer.name} — Druckjob enqueued (#${(data && data.id) || '?'})`, 'success');
    }
    return { ok: true, backend: 'cloud', jobId: data && data.id };
  }
};

function pickBackend(printer) {
  const b = (printer && printer.backend) || 'mock';
  if (Backends[b]) return Backends[b];
  console.warn('[printer-adapter] Unbekanntes Backend, fallback mock:', b);
  return Backends.mock;
}

function labelForKind(k) {
  return { receipt: 'Kassenbon', kitchen: 'Küchen-Bon', correction: 'Stornobeleg', test: 'Testdruck' }[k] || k;
}

// -----------------------------------------------------------------------------
// Öffentliche Print-Funktionen
// -----------------------------------------------------------------------------

async function printToAll(kind, printers, jobBuilder, opts) {
  if (!printers || printers.length === 0) {
    console.info('[printer-adapter] Keine aktiven Drucker für', kind);
    return [];
  }
  const results = [];
  for (const printer of printers) {
    try {
      const job = await jobBuilder(printer);
      const fn  = pickBackend(printer);
      const res = await fn(printer, job, opts || {});
      results.push({ printerId: printer.id, ok: true, result: res });
    } catch (e) {
      console.error('[printer-adapter] print failed', printer.id, e);
      if (!opts || !opts.silent) {
        showToast(`Druck fehlgeschlagen (${printer.name}): ${e.message || e}`, 'error');
      }
      results.push({ printerId: printer.id, ok: false, error: e.message || String(e) });
    }
  }
  return results;
}

export async function printReceipt(order, opts = {}) {
  const printers = listPrinters().filter(p => p.isActive && p.kind === 'receipt');
  const meta     = getRestaurantMeta();
  return printToAll('receipt', printers, async (p) => {
    const out = renderReceiptEscpos(order, meta, { widthMm: p.paperWidthMm || 80 });
    return { kind: 'receipt', orderId: order && (order.id || order.orderId), ...out };
  }, opts);
}

export async function printKitchenTicket(order, opts = {}) {
  const printers = listPrinters().filter(p => p.isActive && p.kind === 'kitchen');
  return printToAll('kitchen', printers, async (p) => {
    const out = renderKitchenTicketEscpos(order, { widthMm: p.paperWidthMm || 80 });
    return { kind: 'kitchen', orderId: order && (order.id || order.orderId), ...out };
  }, opts);
}

/**
 * S63 Block S4 — Teilstorno-Korrekturbeleg an alle aktiven Receipt-Drucker.
 * Wird aus openCorrectionReceiptModal getriggert (zusätzlich zum HTML-Modal).
 * Nutzt renderPartialCorrectionEscpos statt der regulären receipt-Pipeline,
 * damit der Korrekturbeleg semantisch sauber als Storno markiert ist (negative
 * Beträge, BSI-QR der Storno-Tx, Original-Rg.-Referenz).
 */
export async function printPartialCorrectionReceipt(originalOrder, createdCorrections, reasonText, sig, opts = {}) {
  const printers = listPrinters().filter(p => p.isActive && p.kind === 'receipt');
  const meta     = getRestaurantMeta();
  return printToAll('receipt', printers, async (p) => {
    const out = renderPartialCorrectionEscpos(originalOrder, createdCorrections, reasonText, sig, meta, { widthMm: p.paperWidthMm || 80 });
    return {
      kind: 'receipt',
      orderId: originalOrder && (originalOrder.id || originalOrder.orderId),
      isCorrection: true,
      ...out
    };
  }, opts);
}

export async function testPrint(printerId) {
  const printer = getPrinter(printerId);
  if (!printer) {
    showToast('Drucker nicht gefunden', 'error');
    return { ok: false, error: 'printer not found' };
  }
  const job = renderTestPageEscpos(printer);
  const fn  = pickBackend(printer);
  try {
    const res = await fn(printer, { kind: 'test', ...job }, {});
    return { ok: true, result: res };
  } catch (e) {
    showToast(`Testdruck fehlgeschlagen: ${e.message || e}`, 'error');
    return { ok: false, error: e.message || String(e) };
  }
}

export async function enqueue(printerId, jobPayload) {
  const printer = getPrinter(printerId);
  if (!printer) throw new Error('Drucker nicht gefunden: ' + printerId);
  const fn = pickBackend(printer);
  return fn(printer, jobPayload, { silent: true });
}

// -----------------------------------------------------------------------------
// Helper für Settings-UI: nächste freie Slot-ID generieren
// -----------------------------------------------------------------------------
export function newPrinterSlotId() {
  // pr_<8-char-hex>; reicht für den MVP, da pro Tenant nur eine Handvoll
  return 'pr_' + Math.random().toString(16).slice(2, 10);
}

export const PRINTER_KINDS = [
  { id: 'receipt', label: 'Bon-/Theke-Drucker' },
  { id: 'kitchen', label: 'Küchen-Drucker' }
];

export const PRINTER_BACKENDS = [
  { id: 'mock',   label: 'Mock (Preview im Cockpit)',     placeholder: '—' },
  { id: 'bridge', label: 'Bridge (lokaler WebSocket)',    placeholder: 'ws://192.168.1.42:9100' },
  { id: 'cloud',  label: 'Cloud (CloudPRNT / Connect)',   placeholder: 'Device-ID des Druckers' }
];

// -----------------------------------------------------------------------------
// Backward-Compat
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    printerListPrinters: listPrinters,
    printerGetRouting:   getRouting,
    printerPrintReceipt: printReceipt,
    printerPrintKitchen: printKitchenTicket,
    // S63 Block S4 — Teilstorno-Bon
    printerPrintPartialCorrection: printPartialCorrectionReceipt,
    printerTestPrint:    testPrint,
    printerEnqueue:      enqueue
  });
}
